import { ShieldCheck, CheckCircle, DollarSign, Home, ThumbsUp, Ruler, CalendarClock, Hammer, Users, Wrench } from 'lucide-react';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/pavers.js';



export default function PaversPage() {
  const service = {
    title: 'Paver Installation & Repair',
    image: '/hero/best-pavers-hardcaping-services-in-los-angeles.webp',
    description: 'Paver installation & repair by Modern Build. Durable, custom-designed outdoor surfaces. Free estimate: (323) 300-4130',
    link: '/services/pavers',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

      // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "Pavers Services", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
    <>
      <Head>
        <title>Paver Installation & Repair | Free Estimate | Modern Build</title>
        <meta name="title" content="Paver Installation & Repair | Free Estimate | Modern Build" />
        <meta name="description" content="Paver installation & repair by Modern Build. Durable, custom-designed outdoor surfaces. Free estimate: (323) 300-4130" />
        <meta name="keywords" content="paver installation, outdoor hardscaping, paver repair, patio paving, walkway pavers, driveway pavers, los angeles paver contractor, modern build" />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/pavers" />

        <meta property="og:title" content="Paver Installation & Repair | Free Estimate | Modern Build" />
        <meta property="og:description" content="Paver installation & repair by Modern Build. Durable, custom-designed outdoor surfaces. Free estimate: (323) 300-4130" />
        <meta property="og:image" content="https://modernbuildremodel.com/hero/best-pavers-hardcaping-services-in-los-angeles.webp" />
        <meta property="og:url" content="https://modernbuildremodel.com/services/pavers" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Paver Installation & Repair | Free Estimate | Modern Build" />
        <meta name="twitter:description" content="Paver installation & repair by Modern Build. Durable, custom-designed outdoor surfaces. Free estimate: (323) 300-4130" />
        <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-pavers-hardcaping-services-in-los-angeles.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />


        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Paver Installation & Repair | Free Estimate | Modern Build',
                description: 'Expert paver installation and repair services in Los Angeles. Enhance your outdoor spaces with durable, custom-designed pavers.',
                slug: '/services/pavers'
              })
            )
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>

      <Header />
      <Hero category="pavers" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="PAVER SERVICES"
        title="Transform Your Outdoors with Beautiful Pavers"
        background="white"
        description="Modern Build specializes in paver installation and repair. Whether you’re upgrading your patio, driveway, or walkways, we deliver durable, aesthetic solutions tailored to your space."
        extraText="We use high-quality materials and expert craftsmanship to ensure long-lasting beauty and function."
        phone="(323) 300-4130"
        image1="/split/paver-installation-los-angeles.webp"
        image2="/split/paver-repair-los-angeles.webp"
      />

      <Benefits
        title="What’s Included in Our Paver Services?"
        subtitle="Built to last, designed to impress"
        items={[
          {
            icon: <Hammer className="w-8 h-8 text-primary" />,
            title: "Patios, Driveways & Walkways",
            href: "/services/outdoor-living",
          },
          {
            icon: <ShieldCheck className="w-8 h-8 text-primary" />,
            title: "Custom Paver Design",
          },
          {
            icon: <Ruler className="w-8 h-8 text-primary" />,
            title: "Expert Installation & Repairs",
          },
          {
            icon: <CheckCircle className="w-8 h-8 text-primary" />,
            title: "Durable, Weather-Resistant Materials",
          },
          {
            icon: <Home className="w-8 h-8 text-primary" />,
            title: "Seamless Integration with Landscaping",
          },
          {
            icon: <Wrench className="w-8 h-8 text-primary" />,
            title: "Paver Repair & Maintenance Services",
          },
          {
            icon: <DollarSign className="w-8 h-8 text-primary" />,
            title: "Flexible Financing Available",
            href: "/financing",
          },
          {
            icon: <ThumbsUp className="w-8 h-8 text-primary" />,
            title: "Work Performed by Licensed, Bonded & Insured Contractor",
            href: "/about-us",
          },
        ]}
      />


      <ContentBlock
        subtitle="LOS ANGELES PAVERS EXPERTS"
        title="Enhance Your Outdoor Living Space"
        description="Our paver installation services add beauty, functionality, and value to your outdoor spaces. Whether it's a custom patio, driveway, or walkway, Modern Build brings your vision to life with precision and care."
        extraText="We provide a full range of services from initial design to final installation, ensuring that your outdoor space is both stunning and durable."
        buttonText="Request a Free Paver Estimate"
        background="white"
      />

      <ProcessRoadmap
        title="Our Paver Installation & Repair Process"
        subtitle="Quality from the ground up"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Free On-Site Evaluation",
            description: "We assess your outdoor space and discuss design options, material choices, and project scope."
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Design & Material Selection",
            description: "Our experts help you choose the perfect pavers and layout for your outdoor space."
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Installation & Construction",
            description: "Our team installs the pavers with precision, ensuring a durable and beautiful result."
          },
          {
            icon: <CheckCircle className="w-10 h-10 text-primary" />,
            title: "Inspection & Sign-Off",
            description: "We conduct a final inspection to ensure the project meets your expectations and all standards."
          }
        ]}
      />

      <Split
        subtitle="BUILT TO LAST"
        title="Create a Beautiful Outdoor Space"
        background="white"
        description="Whether you're looking to add a stylish patio or update your driveway, our paver services provide long-lasting beauty and function."
        extraText="Let us help you transform your outdoor living space."
        phone="(323) 300-4130"
        image1="/split/paver-outdoor-los-angeles.webp"
        image2="/split/paver-driveway-los-angeles.webp"
        reverse={true}
      />

      <CTASection
        title="Ready to Transform Your Outdoors?"
        subtitle="Free Consultation + Financing Available"
        buttonText="Book Your Free Estimate"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="pavers" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}
